import { memo } from 'react'
import { Handle, Position, type Node, type NodeProps } from '@xyflow/react'
import { MessageSquare } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import {
  getMemoryNodeBorderClass,
  getMemoryNodeHeight,
  MEMORY_NODE_WIDTH,
  type MemoryNodeData,
} from '@/utils/graph-data'
import { useMemoryThreadNavigation } from './use-memory-thread-navigation'

function MemoryGraphNodeComponent({ data, selected }: NodeProps<Node<MemoryNodeData>>) {
  const { memory } = data
  const { openThread, isThreadOpen } = useMemoryThreadNavigation(memory.thread_id)

  const stop = (e: React.MouseEvent) => {
    e.stopPropagation()
  }

  return (
    <div
      className={cn(
        'rounded-md border-2 bg-card px-2 py-1.5 shadow-sm',
        getMemoryNodeBorderClass(data.memory_type),
        selected && 'ring-2 ring-primary ring-offset-2 ring-offset-background',
      )}
      style={{ width: MEMORY_NODE_WIDTH, minHeight: getMemoryNodeHeight(memory) }}
    >
      <Handle type="target" position={Position.Left} className="!h-2 !w-2 !bg-muted-foreground" />
      <div className="flex items-start gap-0.5">
        <p className="line-clamp-2 flex-1 text-xs leading-snug text-foreground">{data.label}</p>
        {memory.thread_id ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="nodrag nopan inline-flex shrink-0">
                <Button
                  type="button"
                  variant="ghost"
                  size="iconXs"
                  className="h-6 w-6"
                  disabled={isThreadOpen}
                  aria-label={isThreadOpen ? 'Thread already open' : 'Open source thread'}
                  onClick={(e) => {
                    stop(e)
                    openThread(e)
                  }}
                >
                  <MessageSquare className="h-3.5 w-3.5" />
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>
              {isThreadOpen ? 'Thread already open' : 'Open source thread'}
            </TooltipContent>
          </Tooltip>
        ) : null}
      </div>
      {memory.source_message_text ? (
        <blockquote
          className="nodrag nopan mt-1 line-clamp-2 rounded border border-border/60 bg-muted/40 px-2 py-1 text-[10px] italic leading-snug text-muted-foreground"
          title={memory.source_message_text}
        >
          {memory.source_message_text}
        </blockquote>
      ) : null}
      <div className="mt-1 flex flex-wrap gap-1">
        <Badge variant="secondary" className="h-4 px-1 text-[10px]">
          {data.memory_type}
        </Badge>
        <Badge variant="outline" className="h-4 px-1 text-[10px]">
          {data.importance}
        </Badge>
      </div>
      <Handle type="source" position={Position.Right} className="!h-2 !w-2 !bg-muted-foreground" />
    </div>
  )
}

export const MemoryGraphNode = memo(MemoryGraphNodeComponent)
